import React, { useState, useId } from 'react';
import { colors } from '@/assets/styles/colors';
import { InfoBubble } from '@/components/ui/tooltips/InfoBubble';

// Track: 35px wide × 14px tall
// Thumb: 20px × 20px (overflows track vertically, centered)
// OFF → thumb at left: -3px; ON → thumb at left: 18px (35 - 20 + 3)
const TRACK_WIDTH = 35;
const TRACK_HEIGHT = 14;
const THUMB_SIZE = 20;
const THUMB_OFFSET = 3;
const THUMB_ON_LEFT = TRACK_WIDTH - THUMB_SIZE + THUMB_OFFSET;
const THUMB_OFF_LEFT = -THUMB_OFFSET;

export interface SwitchProps {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  label?: React.ReactNode;
  tooltipText?: string;
  className?: string;
  'data-testid'?: string;
}

export const Switch: React.FC<SwitchProps> = ({
  checked,
  defaultChecked = false,
  disabled = false,
  onChange,
  label,
  tooltipText,
  className = '',
  'data-testid': dataTestId,
}) => {
  const generatedId = useId();
  const isControlled = typeof checked === 'boolean';
  const [internal, setInternal] = useState<boolean>(defaultChecked);

  const isOn = isControlled ? checked : internal;

  const trackColor = isOn ? colors.feedback.success200_2 : colors.grays.neutralCC;
  const thumbColor = isOn ? colors.feedback.success300 : colors.grays.neutralEE;
  const thumbLeft = isOn ? THUMB_ON_LEFT : THUMB_OFF_LEFT;

  const handleClick = () => {
    if (disabled) return;
    const newValue = !isOn;
    if (!isControlled) {
      setInternal(newValue);
    }
    onChange?.(newValue);
  };

  return (
    <label
      htmlFor={generatedId}
      className={`inline-flex items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${className}`}
      data-testid={dataTestId}
    >
      <div
        role="switch"
        aria-checked={isOn}
        aria-disabled={disabled}
        id={generatedId}
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        onKeyDown={(event) => {
          if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            handleClick();
          }
        }}
        style={{
          position: 'relative',
          width: `${TRACK_WIDTH}px`,
          height: `${TRACK_HEIGHT}px`,
          borderRadius: `${TRACK_HEIGHT / 2}px`,
          backgroundColor: trackColor,
          transition: 'background-color 200ms ease',
          flexShrink: 0,
        }}
        data-testid={dataTestId ? `${dataTestId}-track` : undefined}
      >
        <span
          style={{
            position: 'absolute',
            top: '50%',
            left: `${thumbLeft}px`,
            transform: 'translateY(-50%)',
            width: `${THUMB_SIZE}px`,
            height: `${THUMB_SIZE}px`,
            borderRadius: '50%',
            backgroundColor: thumbColor,
            boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
            transition: 'left 200ms ease, background-color 200ms ease',
          }}
          data-testid={dataTestId ? `${dataTestId}-thumb` : undefined}
        />
      </div>
      {label && (
        <span
          style={{ color: disabled ? colors.grays.neutralCC : colors.grays.neutral33 }}
          className="text-sm select-none"
        >
          {label}
          {tooltipText && <InfoBubble text={tooltipText} />}
        </span>
      )}
    </label>
  );
};
